import {
  ExpandIcon,
  PencilIcon,
  RefreshCwIcon,
  SparklesIcon,
} from "lucide-react";

import { GenerateRubricInfoGuide } from "./GenerateRubricInfoGuide";
import { Column, RichTextDisplay, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { cn } from "@/lib/utils";
import { JobInternalConfig as JobInternalConfigType } from "@/types";

export const RatingRubricContent = ({
  jobInternalConfig,
  maxRubricHeight,
  onExpandClick,
  onEditClick,
  onRegenerateClick,
}: {
  jobInternalConfig: JobInternalConfigType;
  maxRubricHeight?: string;
  onExpandClick?: () => void;
  onEditClick: () => void;
  onRegenerateClick: () => void;
}) => {
  return (
    <Column className="gap-4 rounded-lg border border-slate-200 p-4">
      {/* Header with action buttons */}
      <Row className="items-center justify-between border-b border-slate-200 pb-4">
        <Row className="items-center gap-2">
          <SparklesIcon className="h-4 w-4 text-primary-700" />
          <Text className="text-base font-semibold text-slate-800">
            Rating Rubric
          </Text>
        </Row>
        <TooltipProvider delayDuration={0}>
          <Row className="items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button onClick={onRegenerateClick} size="icon" variant="ghost">
                  <RefreshCwIcon className="h-4.5 w-4.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                Edit human input and regenerate rubric
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button onClick={onEditClick} size="icon" variant="ghost">
                  <PencilIcon className="h-4.5 w-4.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                Edit rubric text directly
              </TooltipContent>
            </Tooltip>
            {onExpandClick && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button onClick={onExpandClick} size="icon" variant="ghost">
                    <ExpandIcon className="h-4.5 w-4.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  Expand rubric view
                </TooltipContent>
              </Tooltip>
            )}
          </Row>
        </TooltipProvider>
      </Row>

      {/* Human Input (read-only) */}
      <Column className="gap-2">
        <Row className="items-center gap-1">
          <Text className="text-sm font-medium text-slate-700">
            Human Input
          </Text>
          <GenerateRubricInfoGuide />
        </Row>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <RichTextDisplay
            richText={jobInternalConfig.autoProfileRatingHumanInput ?? ""}
          />
        </div>
      </Column>

      {/* APR Rubric (read-only) */}
      <Column className="gap-2">
        <Row className="items-center gap-1">
          <Text className="text-sm font-medium text-slate-700">APR Rubric</Text>
          <GenerateRubricInfoGuide variant="apr-rubric" />
        </Row>
        <div
          className={cn(
            "overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-3",
            maxRubricHeight,
          )}
        >
          <RichTextDisplay
            richText={jobInternalConfig.autoProfileRatingRubric ?? ""}
          />
        </div>
      </Column>
    </Column>
  );
};
